import { Star } from "lucide-react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useStarredOrderIds, useToggleOrderStar } from "@/hooks/useOrderStars";

interface StarOrderButtonProps {
  orderId: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeMap = {
  sm: { btn: "h-7 w-7", icon: "h-3.5 w-3.5" },
  md: { btn: "h-8 w-8", icon: "h-4 w-4" },
  lg: { btn: "h-9 w-9", icon: "h-[18px] w-[18px]" },
};

export function StarOrderButton({ orderId, size = "md", className }: StarOrderButtonProps) {
  const { data: starred } = useStarredOrderIds();
  const toggle = useToggleOrderStar();
  const isStarred = starred?.has(orderId) ?? false;
  const { btn, icon } = sizeMap[size];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(btn, "text-muted-foreground hover:text-foreground", className)}
          aria-label={isStarred ? "Unsubscribe" : "Subscribe"}
          aria-pressed={isStarred}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggle.mutate({ orderId, starred: isStarred });
          }}
        >
          <Star
            className={cn(
              icon,
              "transition-colors",
              isStarred && "fill-amber-400 text-amber-400"
            )}
          />
        </Button>
      </TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipContent side="bottom" className="z-[100]">
          {isStarred ? "Unsubscribe" : "Subscribe"}
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}